import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

import { trackEvent } from "../utils/analytics";
import { accentColor, sanitizeWidgetConfig, widgetReportUrl } from "../utils/widgetConfig";

const labelOf = (item) => item?.label || item?.ban_id?.split(" ").slice(0, -2).join(" ") || item?.ban_id || "";

export default function WidgetAddressPage() {
  const location = useLocation();
  const config = useMemo(
    () => sanitizeWidgetConfig(Object.fromEntries(new URLSearchParams(location.search))),
    [location.search]
  );
  const [query, setQuery] = useState(config.city);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const aborter = useRef(null);
  const started = useRef(false);
  const root = useRef(null);
  const accent = accentColor(config.accent);

  useEffect(() => {
    trackEvent("widget_impression", { partner: config.partner, widget_theme: config.theme });
  }, [config.partner, config.theme]);

  useEffect(() => {
    const sendHeight = () => {
      const height = Math.ceil(root.current?.getBoundingClientRect().height || document.documentElement.scrollHeight);
      window.parent.postMessage({ type: "etatdulieu:resize", height }, "*");
    };
    sendHeight();
    const observer = new ResizeObserver(sendHeight);
    if (root.current) observer.observe(root.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => {
    clearTimeout(timer.current);
    aborter.current?.abort();
  }, []);

  const search = (value) => {
    const q = value.trim();
    clearTimeout(timer.current);
    aborter.current?.abort();
    if (q.length < 2) { setItems([]); return; }
    timer.current = setTimeout(async () => {
      aborter.current = new AbortController();
      setLoading(true);
      try {
        const response = await fetch(`/api/autocomplete?q=${encodeURIComponent(q)}&limit=7`, { signal: aborter.current.signal });
        setItems(response.ok ? await response.json() : []);
      } catch (error) {
        if (error.name !== "AbortError") setItems([]);
      } finally { setLoading(false); }
    }, 300);
  };

  const openReport = (item = selected) => {
    if (!item?.ban_id) return;
    const url = widgetReportUrl(item.ban_id, config, window.location.origin);
    trackEvent("widget_report_opened", { partner: config.partner });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const select = (item) => {
    setSelected(item);
    setQuery(labelOf(item));
    setItems([]);
    trackEvent("widget_address_selected", { partner: config.partner });
    openReport(item);
  };

  return (
    <main ref={root} className={`edl-widget theme-${config.theme}`} style={{ "--accent": accent }}>
      <Helmet>
        <title>Widget adresse — État du Lieu</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <style>{`
        html,body,#root{margin:0;min-height:0;background:transparent}.edl-widget{box-sizing:border-box;width:100%;padding:18px;color:#172033;background:#fff;border:1px solid #dbe4f0;border-radius:16px;font-family:system-ui,-apple-system,"Segoe UI",sans-serif}.theme-dark{color:#f8fafc;background:#111827;border-color:#334155}.theme-auto{color:#172033;background:#fff}.brand{font-size:13px;font-weight:800;color:var(--accent);text-decoration:none}.edl-widget h1{font-size:20px;line-height:1.25;margin:8px 0 14px}.search-row{display:flex;gap:8px}.search-box{position:relative;flex:1}.search-box input{box-sizing:border-box;width:100%;min-height:44px;padding:10px 12px;border:1px solid #aebdd0;border-radius:10px;font-size:16px;background:#fff;color:#0f172a}.theme-dark .search-box input{background:#1f2937;color:#fff;border-color:#64748b}.results{position:absolute;z-index:10;left:0;right:0;top:48px;margin:0;padding:5px;list-style:none;background:#fff;border:1px solid #cbd5e1;border-radius:10px;box-shadow:0 12px 30px #0003}.theme-dark .results{background:#1f2937}.results button{display:block;width:100%;padding:9px;border:0;border-radius:7px;text-align:left;background:transparent;color:inherit;cursor:pointer}.results button:hover,.results button:focus{background:#e8efff}.theme-dark .results button:hover,.theme-dark .results button:focus{background:#334155}.validate{min-height:44px;padding:0 15px;border:0;border-radius:10px;background:var(--accent);color:#fff;font-weight:800;cursor:pointer}.validate:disabled{opacity:.45;cursor:not-allowed}.note{margin:13px 0 0;font-size:12px;color:#64748b}.theme-dark .note{color:#cbd5e1}.site-link{color:inherit}@media(max-width:440px){.search-row{display:block}.validate{width:100%;margin-top:8px}}@media(prefers-color-scheme:dark){.theme-auto{color:#f8fafc;background:#111827;border-color:#334155}.theme-auto .search-box input{background:#1f2937;color:#fff;border-color:#64748b}.theme-auto .results{background:#1f2937}.theme-auto .note{color:#cbd5e1}}
      `}</style>
      <a className="brand" href="https://etatdulieu.com" target="_blank" rel="noreferrer">État du Lieu</a>
      <h1>{config.title}</h1>
      <div className="search-row">
        <div className="search-box">
          <input
            value={query}
            aria-label="Adresse"
            placeholder="Saisissez une adresse en France"
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value); setSelected(null); search(event.target.value);
              if (!started.current) { started.current = true; trackEvent("widget_search_started", { partner: config.partner }); }
            }}
          />
          {items.length > 0 && <ul className="results">{items.map((item, index) => <li key={item.ban_id || index}><button type="button" onClick={() => select(item)}>{labelOf(item)}</button></li>)}</ul>}
        </div>
        <button className="validate" type="button" disabled={!selected || loading} onClick={() => openReport()}>{loading ? "Recherche…" : "Vérifier"}</button>
      </div>
      <p className="note">Service gratuit basé sur des données publiques françaises · <a className="site-link" href="https://etatdulieu.com" target="_blank" rel="noreferrer">En savoir plus</a></p>
    </main>
  );
}
